// App.tsx
import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import wildlifeDb from './data/wildlife_db2.json';
import { useSound } from './hooks/useSound';
import Footer from './components/Footer';

// Define types for the wildlife database
interface WildlifeDB {
  wordsByLength: {
    [key: string]: string[];
  };
}

// Type assertion for the imported JSON
const typedWildlifeDb = wildlifeDb as unknown as WildlifeDB;
import './App.css';

// Types
type GamePhase = 'modeSelect' | 'pickNumbers' | 'memorize' | 'gameActive' | 'results';
type GameMode = 'classic' | 'daily';
type Difficulty = 'easy' | 'normal' | 'hard';

interface Word {
  word: string;
  length: number;
}

interface GameState {
  score: number;
  streak: number;
  bestStreak: number;
  timeRemaining: number;
  timerActive: boolean;
}

// Helper function to generate word pattern with revealed letters
const getWordPattern = (word: string): string => {
  const length = word.length;
  
  if (length <= 7) {
    // For words 7 characters or shorter: reveal first letter only
    return word[0] + '_'.repeat(length - 1);
  } else if (length <= 10) {
    // For words 8-10 characters: reveal first letter and one more letter
    const revealPosition = Math.floor(length / 2); // Reveal middle letter
    return word.split('').map((char, index) => 
      index === 0 || index === revealPosition ? char : '_'
    ).join('');
  } else {
    // For words longer than 10 characters: reveal first letter and two more letters
    const revealPosition1 = Math.floor(length / 3); // First third
    const revealPosition2 = Math.floor(length * 2 / 3); // Second third
    return word.split('').map((char, index) => 
      index === 0 || index === revealPosition1 || index === revealPosition2 ? char : '_'
    ).join('');
  }
};

// Generate multiple reveal patterns for cycling
const generateRevealPatterns = (word: string): string[] => {
  const length = word.length;
  const patterns: string[] = [];
  
  if (length <= 7) {
    // Short words: only one pattern
    patterns.push(getWordPattern(word));
  } else if (length <= 10) {
    // Medium words: 2-3 patterns cycling through different reveal positions
    const firstLetter = word[0];
    const middlePos = Math.floor(length / 2);
    
    // Pattern 1: First + middle
    patterns.push(word.split('').map((char, index) => 
      index === 0 || index === middlePos ? char : '_'
    ).join(''));
    
    // Pattern 2: First only (for contrast)
    patterns.push(firstLetter + '_'.repeat(length - 1));
    
    // Pattern 3: First + different position if possible
    if (length > 8) {
      const altPos = Math.floor(length * 3 / 4);
      patterns.push(word.split('').map((char, index) => 
        index === 0 || index === altPos ? char : '_'
      ).join(''));
    }
  } else {
    // Long words: 3-4 patterns cycling through different combinations
    const pos1 = Math.floor(length / 3);
    const pos2 = Math.floor(length * 2 / 3);
    const pos3 = Math.floor(length / 2);
    
    // Pattern 1: First + pos1 + pos2
    patterns.push(word.split('').map((char, index) => 
      index === 0 || index === pos1 || index === pos2 ? char : '_'
    ).join(''));
    
    // Pattern 2: First + pos3
    patterns.push(word.split('').map((char, index) => 
      index === 0 || index === pos3 ? char : '_'
    ).join(''));
    
    // Pattern 3: First + pos1
    patterns.push(word.split('').map((char, index) => 
      index === 0 || index === pos1 ? char : '_'
    ).join(''));
    
    // Pattern 4: First + pos2
    patterns.push(word.split('').map((char, index) => 
      index === 0 || index === pos2 ? char : '_'
    ).join(''));
  }
  
  return patterns;
};

function App() {
  // Game state
  const [gamePhase, setGamePhase] = useState<GamePhase>('modeSelect');
  const [gameMode, setGameMode] = useState<GameMode>('classic');
  const [difficulty, setDifficulty] = useState<Difficulty>('normal');
  const [words, setWords] = useState<Word[]>([]);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [guesses, setGuesses] = useState<string[]>([]);
  const [gameState, setGameState] = useState<GameState>({
    score: 0,
    streak: 0,
    bestStreak: 0,
    timeRemaining: 0,
    timerActive: false
  });
  const [theme, setTheme] = useState<'light' | 'dark'>(() => 
    localStorage.getItem('theme') === 'dark' ? 'dark' : 'light'
  );
  const [showHelp, setShowHelp] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [lastDailyPlayed, setLastDailyPlayed] = useState<string | null>(
    localStorage.getItem('lastDailyPlayed')
  );
  const [dailyChallengeAvailable, setDailyChallengeAvailable] = useState(true);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [patternIndices, setPatternIndices] = useState<number[]>([]);
  
  // Check if daily challenge is available
  const checkDailyChallengeAvailability = useCallback(() => {
    if (!lastDailyPlayed) {
      setDailyChallengeAvailable(true);
      return true;
    }
    
    const lastPlayed = new Date(lastDailyPlayed);
    const now = new Date();
    const hoursSinceLastPlay = (now.getTime() - lastPlayed.getTime()) / (1000 * 60 * 60);
    const isAvailable = hoursSinceLastPlay >= 24;
    setDailyChallengeAvailable(isAvailable);
    return isAvailable;
  }, [lastDailyPlayed]);
  
  // Check daily challenge availability on mount and when lastDailyPlayed changes
  useEffect(() => {
    checkDailyChallengeAvailability();
  }, [lastDailyPlayed, checkDailyChallengeAvailability]);
  
  // Start daily challenge
  const startDailyChallenge = () => {
    const canPlay = checkDailyChallengeAvailability();
    if (!canPlay) return;
    
    // Set the current time as last played time
    const now = new Date().toISOString();
    setLastDailyPlayed(now);
    localStorage.setItem('lastDailyPlayed', now);
    
    // Set up daily challenge mode
    setGameMode('daily');
    setGamePhase('pickNumbers');
    
    // Play start sound for daily challenge
    playSound('start');
  };

  // Initialize theme
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (gameState.timerActive && gameState.timeRemaining > 0) {
      timer = setTimeout(() => {
        setGameState(prev => ({
          ...prev,
          timeRemaining: prev.timeRemaining - 1
        }));
      }, 1000);
    } else if (gameState.timeRemaining === 0 && gameState.timerActive) {
      // Only submit if all guesses are filled
      if (guesses.every(guess => guess.trim() !== '')) {
        handleSubmitGuess();
      } else {
        // Just end the game if time runs out with empty guesses
        setGameState(prev => ({
          ...prev,
          timerActive: false
        }));
        setGamePhase('results');
      }
    }
    
    return () => clearTimeout(timer);
  }, [gameState.timeRemaining, gameState.timerActive, guesses]);

// Inside your App component, update the theme effect
useEffect(() => {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }
}, [theme]);

  // Close other modals when one is opened
  useEffect(() => {
    if (showAbout) setShowHelp(false);
  }, [showAbout]);

  useEffect(() => {
    if (showHelp) setShowAbout(false);
  }, [showHelp]);

  // Trigger word reveal animation when game becomes active
  useEffect(() => {
    if (gamePhase === 'gameActive' && words.length > 0) {
      // Initialize pattern indices for all words
      const initialPatternIndices = words.map(() => 0);
      setPatternIndices(initialPatternIndices);
      setCurrentWordIndex(0);
    }
  }, [gamePhase, words]);

  // Cycle through patterns word by word
  useEffect(() => {
    if (patternIndices.length === 0) return;
    
    const interval = setInterval(() => {
      setPatternIndices(prev => {
        const newIndices = [...prev];
        const currentWord = words[currentWordIndex];
        
        if (currentWord) {
          const patterns = generateRevealPatterns(currentWord.word);
          newIndices[currentWordIndex] = (newIndices[currentWordIndex] + 1) % patterns.length;
        }
        
        return newIndices;
      });
      
      // Move to next word
      setCurrentWordIndex(prev => (prev + 1) % words.length);
    }, 1500); // Change pattern every 1.5 seconds
    
    return () => clearInterval(interval);
  }, [patternIndices.length, words, currentWordIndex]);

  // Toggle theme
  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Get random words from wildlife database with the same length
  const getRandomWords = (length: number, count: number): Word[] => {
    // If length is 0 or invalid, return empty array
    if (!length || length < 3) return [];
    
    // Make sure length is within valid range (3-15)
    const validLength = Math.min(Math.max(3, length), 15);
    
    const wordsOfLength = typedWildlifeDb.wordsByLength[validLength.toString()] || [];
    if (!wordsOfLength || wordsOfLength.length < count) return [];
    
    // Shuffle and select 'count' unique words
    const shuffled = [...wordsOfLength].sort(() => 0.5 - Math.random());
    const selectedWords = [];
    const usedWords = new Set();
    
    for (const word of shuffled) {
      if (usedWords.size >= count) break;
      const lowerWord = word.toLowerCase();
      if (!usedWords.has(lowerWord)) {
        usedWords.add(lowerWord);
        selectedWords.push({ word, length: validLength });
      }
    }
    
    return selectedWords;
  };
  
  // Get a random length between min and max (inclusive) that has enough words
  const getValidWordLength = (min: number, max: number): number | null => {
    const validLengths = [];
    for (let len = min; len <= max; len++) {
      const words = typedWildlifeDb.wordsByLength[len.toString()] || [];
      if (words.length >= 3) { // Ensure we have at least 3 words of this length
        validLengths.push(len);
      }
    }
    return validLengths.length > 0 
      ? validLengths[Math.floor(Math.random() * validLengths.length)] 
      : null;
  };

  // Initialize sound effects
  const { playSound } = useSound();

  // Handle number selection
  const handleNumberClick = (num: number) => {
    if (selectedNumbers.length < 2) {
      const newNumbers = [...selectedNumbers, num];
      setSelectedNumbers(newNumbers);

      // Play click sound
      playSound('start');

      if (newNumbers.length === 2) {
        const sum = newNumbers[0] + newNumbers[1];
        
        // Get three words with the same length (sum of the two numbers)
        const targetLength = Math.min(Math.max(3, sum), 15); // Ensure length is between 3-15
        const newWords = getRandomWords(targetLength, 3);
        
        if (newWords.length === 3) {
          setWords(newWords);
          setGuesses(Array(3).fill(''));
          setGamePhase('gameActive');
          startGameTimer();
          playSound('start'); // Play game start sound
        } else {
          // If we couldn't get enough words, try to find another valid length
          const validLength = getValidWordLength(3, 15);
          if (validLength) {
            const fallbackWords = getRandomWords(validLength, 3);
            if (fallbackWords.length === 3) {
              setWords(fallbackWords);
              setGuesses(Array(3).fill(''));
              setGamePhase('gameActive');
              startGameTimer();
              playSound('start'); // Play game start sound
              return;
            }
          }
          
          // If we still can't find words, show an error
          alert('Could not find enough words. Please try again.');
          setSelectedNumbers([]);
        }
      }
    }
  };

  // Start game timer
  const startGameTimer = () => {
    const timeLimit = gameMode === 'daily' ? 60 : 
                     difficulty === 'easy' ? 60 : 
                     difficulty === 'normal' ? 45 : 30;
                     
    setGameState(prev => ({
      ...prev,
      timeRemaining: timeLimit,
      timerActive: true
    }));
  };

  // Handle guess submission

const handleSubmitGuess = () => {
  // Only check for empty guesses if the user manually submits and not all guesses are filled
  if (gamePhase === 'gameActive') {
    const hasEmptyGuesses = guesses.some(guess => !guess.trim());
    if (hasEmptyGuesses) {
      alert('Please fill in all word guesses');
      return;
    }
  }

  // Base points per correct word based on difficulty
  const basePoints = {
    easy: 5,
    normal: 10,
    hard: 15,
    daily: 20
  }[difficulty || 'normal'];

  // Calculate correct answers
  const currentGuesses = guesses.length > 0 ? guesses : Array(words.length).fill('');
  let correctCount = 0;
  const correctWords: boolean[] = [];
  
  words.forEach((word, index) => {
    const guess = currentGuesses[index] || '';
    const isCorrect = word.word.toLowerCase() === guess.toLowerCase().trim();
    if (isCorrect) correctCount++;
    correctWords.push(isCorrect);
  });

  // Base score (points per correct word)
  let roundScore = correctCount * basePoints;

  // Perfect round bonus (1.5x)
  const isPerfectRound = correctCount === 3;
  if (isPerfectRound) {
    roundScore = Math.floor(roundScore * 1.5);
  }

  // Streak bonus (only applies to perfect rounds)
  const newStreak = isPerfectRound ? (gameState.streak + 1) : 0;
  const streakMultiplier = isPerfectRound ? (1 + (newStreak * 0.5)) : 1; // +50% per streak
  roundScore = Math.floor(roundScore * streakMultiplier);

  // Time bonus (1 point per second remaining, up to 30 points)
  const maxTimeBonus = 30;
  const timeBonus = Math.min(gameState.timeRemaining, maxTimeBonus);
  roundScore += timeBonus;

  // Update game state
  const newBestStreak = Math.max(gameState.bestStreak, newStreak);
  const newScore = gameState.score + roundScore;

  // Play appropriate sound
  if (isPerfectRound) {
    playSound('achievement');
  } else if (correctCount > 0) {
    playSound('score');
  }

  // Update game state with new scores
  setGameState(prev => ({
    ...prev,
    score: newScore,
    streak: newStreak,
    bestStreak: newBestStreak,
    timerActive: false
  }));
  
  setGamePhase('results');
};

  // Get time until next daily challenge is available
  const getTimeUntilNextDailyChallenge = (): string => {
    if (!lastDailyPlayed) return 'Now';
    
    const lastPlayed = new Date(lastDailyPlayed);
    const now = new Date();
    const nextAvailable = new Date(lastPlayed.getTime() + 24 * 60 * 60 * 1000);
    const diffMs = nextAvailable.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Now';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} minutes`;
  };

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Reset game
  const resetGame = () => {
    setSelectedNumbers([]);
    setWords([]);
    setGuesses([]);
    setCurrentWordIndex(0);
    setPatternIndices([]);
    setGamePhase('modeSelect');
  };

  return (
    <div className={`min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors`}>
    <Helmet>
      <title>WordGame Puzzle: Wildlife Fun Awaits</title>
      <meta name="description" content="Explore our free wordgame and puzzle collection with wildlife themes! Test your skills and enjoy playing wildlife games online today." />
      <meta name="theme-color" content={theme === 'dark' ? '#1f2937' : '#f3f4f6'} />
    </Helmet>
    
    {/* Header */}
    <header className="bg-white dark:bg-gray-800 shadow">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">WordGame</h1>
          <div className="flex items-center space-x-4">
            <div className="hidden sm:flex items-center space-x-6">
              <div className="text-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">Score</div>
                <div className="font-bold">{gameState.score}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">Best Streak</div>
                <div className="font-bold">{gameState.bestStreak}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">Time</div>
                <div className="font-mono font-bold">
                  {formatTime(gameState.timeRemaining)}
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className={`flex items-center justify-center h-8 w-8 rounded-full border-2 ${showHelp ? 'border-blue-500' : 'border-current'} text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white`}
              title="How to play"
            >
              <span className="font-bold text-sm">i</span>
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
        
        {showHelp && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
            <h3 className="font-semibold mb-2">How to Play</h3>
            <ul className="list-disc pl-5 space-y-1 mb-3">
              <li>Select two numbers to generate wildlife-related words</li>
              <li>Type the words you see</li>
              <li>Earn 10 points for each correct answer</li>
              <li>Get all 3 words right to continue your streak!</li>
            </ul>
            
            <div className="mt-3 pt-3 border-t border-blue-100 dark:border-blue-800">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">Privacy & Terms</h4>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Your game progress is stored locally in your browser. We don't collect personal data. 
                By playing, you agree to our <a href="/privacy" className="underline hover:text-blue-900 dark:hover:text-blue-100">Privacy Policy</a> and 
                <a href="/terms" className="underline hover:text-blue-900 dark:hover:text-blue-100 ml-1">Terms of Service</a>.
              </p>
            </div>
          </div>
        )}
      </div>
    </header>

    {/* Modals */}
    {showAbout && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">About WildlifeGamesOnline</h3>
              <button 
                onClick={() => setShowAbout(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors p-1 -mt-1 -mr-1"
                aria-label="Close"
                title="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-6 text-gray-700 dark:text-gray-300">
              <div className="text-center mb-6">
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Bringing fun and education together through wildlife games
                </p>
              </div>

              <section>
                <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Our Mission</h4>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  At WildlifeGamesOnline, we're passionate about wildlife conservation and education. 
                  We believe that learning about animals and their habitats should be fun and engaging 
                  for people of all ages.
                </p>
              </section>

              <section>
                <h4 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Our Story</h4>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Founded in 2024, WildlifeGamesOnline started as a small project to create educational 
                  games about wildlife. Today, we've grown into a platform with thousands of users who 
                  share our passion for wildlife and conservation.
                </p>
              </section>

              <section>
                <h4 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">Our Team</h4>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Our team consists of wildlife enthusiasts, educators, and game developers who are 
                  dedicated to creating high-quality, educational content that makes a difference.
                </p>
              </section>

            </div>
          </div>
        </div>
      </div>
    )}

    {/* Main Content */}
    <main className="flex-grow container mx-auto px-4 py-8">
      {gamePhase === 'modeSelect' && (
        <div className="text-center max-w-md mx-auto">
          <h2 className="text-3xl font-bold mb-8">Select Game Mode</h2>
          <div className="space-y-6">
            {/* Classic Mode - Free Play */}
            <div className="w-full">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold mb-4">Classic Mode</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                  Play anytime, no limits. Choose your difficulty level:
                </p>
                <div className="flex space-x-2 justify-center mb-4">
                  {(['easy', 'normal', 'hard'] as const).map((diff) => (
                    <button
                      key={diff}
                      onClick={() => {
                        setDifficulty(diff);
                        setGameMode('classic');
                        setGamePhase('pickNumbers');
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        difficulty === diff 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {diff.charAt(0).toUpperCase() + diff.slice(1)}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setGameMode('classic');
                    setGamePhase('pickNumbers');
                  }}
                  className="w-full py-3 px-6 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-lg font-medium"
                >
                  Start Classic Game
                </button>
              </div>
            </div>
            
            {/* Daily Challenge */}
            <div className="w-full">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border-2 border-green-500/30">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-semibold">Daily Challenge</h3>
                  <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">
                    {dailyChallengeAvailable ? 'Available Now' : 'On Cooldown'}
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                  Test your skills with a new challenge every 24 hours!
                </p>
                <button
                  onClick={startDailyChallenge}
                  disabled={!dailyChallengeAvailable}
                  className={`w-full py-3 px-6 rounded-lg transition-colors text-lg font-medium ${
                    dailyChallengeAvailable
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {dailyChallengeAvailable ? 'Start Daily Challenge' : 'Challenge Locked'}
                </button>
                {!dailyChallengeAvailable && lastDailyPlayed && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    Next challenge in: {getTimeUntilNextDailyChallenge()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {gamePhase === 'pickNumbers' && (
        <div className="max-w-md mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">
            {gameMode === 'daily' 
              ? 'Daily Challenge - Select Two Numbers' 
              : 'Select Two numbers to play the sum.'}
          </h2>
          <div className="grid grid-cols-5 gap-3 mb-8">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num) => (
              <button
                key={num}
                onClick={() => handleNumberClick(num)}
                disabled={selectedNumbers.includes(num)}
                className={`p-4 text-lg font-bold rounded-lg transition-colors ${
                  selectedNumbers.includes(num)
                    ? 'bg-blue-500 text-white'
                    : 'bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
          {selectedNumbers.length > 0 && (
            <div className="text-center text-gray-600 dark:text-gray-400">
              Selected: {selectedNumbers.join(' + ')} = {selectedNumbers.reduce((a, b) => a + b, 0)} letters
            </div>
          )}
        </div>
      )}

      {gamePhase === 'gameActive' && words.length > 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Type the words:</h2>
              <div className="text-xl font-mono font-bold px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                {formatTime(gameState.timeRemaining)}
              </div>
            </div>

            <div className="space-y-6">
              {words.map((word, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Word {index + 1}</h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {word.length} letters
                    </span>
                  </div>
                  
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg font-mono text-center text-xl tracking-wider mb-2 word-pattern-container">
                    {(() => {
                      const patterns = generateRevealPatterns(word.word);
                      const currentPatternIndex = patternIndices[index] || 0;
                      const currentPattern = patterns[currentPatternIndex];
                      const useStars = word.word.length > 9;
                      
                      return currentPattern.split('').map((char, charIndex) => (
                        <span 
                          key={charIndex} 
                          className={`inline-block w-6 text-center transition-all duration-300 ${
                            char !== '_' 
                              ? 'letter-revealed' 
                              : 'star-placeholder'
                          }`}
                        >
                          {char !== '_' ? (
                            char.toUpperCase()
                          ) : (
                            useStars ? '✱' : '—'
                          )}
                        </span>
                      ));
                    })()}
                  </div>
                  <input
                    type="text"
                    value={guesses[index] || ''}
                    onChange={(e) => {
                      const newGuesses = [...guesses];
                      newGuesses[index] = e.target.value;
                      setGuesses(newGuesses);
                    }}
                    className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Type the ${word.length}-letter word`}
                    maxLength={word.length}
                    autoFocus={index === 0}
                  />
                </div>
              ))}
            </div>

            <button
              onClick={handleSubmitGuess}
              className="w-full mt-8 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
            >
              Submit Answers
            </button>
          </div>
        </div>
      )}

      {gamePhase === 'results' && words.length > 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-center mb-6">
              {gameState.streak > 0 
                ? `🔥 Streak: ${gameState.streak} ${'!'.repeat(Math.min(gameState.streak, 3))}` 
                : 'Round Complete!'}
            </h2>
            
            <div className="space-y-6 mb-8">
              {words.map((word, index) => {
                const isCorrect = word.word.toLowerCase() === guesses[index]?.toLowerCase().trim();
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold">Word {index + 1}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isCorrect 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {isCorrect ? 'Correct! +10' : 'Incorrect'}
                      </span>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="font-mono text-center text-lg mb-2">
                        {word.word.split('').map((char, i) => (
                          <span 
                            key={i} 
                            className={`inline-block w-6 text-center ${
                              isCorrect || (i < guesses[index]?.length && guesses[index][i].toLowerCase() === char.toLowerCase())
                                ? 'text-green-600 dark:text-green-400'
                                : !isCorrect && i < guesses[index]?.length
                                  ? 'text-red-600 dark:text-red-400 line-through'
                                  : 'text-gray-400'
                            }`}
                          >
                            {char}
                          </span>
                        ))}
                      </div>
                      {!isCorrect && (
                        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                          You entered: {guesses[index] || '(empty)'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={resetGame}
                className="flex-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                Play Again
              </button>
              <button
                onClick={() => {
                  setGameState(prev => ({
                    ...prev,
                    streak: 0
                  }));
                  resetGame();
                }}
                className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                New Game
              </button>
            </div>
          </div>
        </div>
      )}
    </main>

    {/* Footer */}
    <Footer />
  </div>
);
}
export default App;